import React, { useState, useEffect } from 'react';
import { FileText, Eye, Download, Calendar, Tag, Search, X } from 'lucide-react';
import { Document } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

export default function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [showSideBySide, setShowSideBySide] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/documents`);
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      const raw = (await response.json()) as unknown;
      const list = Array.isArray(raw) ? (raw as Document[]) : [];
      setDocuments(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedDocument(null);
    setDocumentUrl(null);
    setShowSideBySide(false);
  };

  const fetchDocumentUrl = async (documentId: number) => {
    try {
      setDocumentLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/view`);
      if (response.ok) {
        // The response is the actual document content, so we can use the URL directly
        setDocumentUrl(`${API_BASE_URL}/api/documents/${documentId}/view`);
      } else {
        const errorData = (await response.json()) as unknown;
        const msg = (errorData && typeof errorData === 'object' && 'message' in (errorData as any))
          ? String((errorData as any).message)
          : 'Failed to load document';
        throw new Error(msg);
      }
    } catch (err) {
      console.error('Error fetching document URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to load document');
    } finally {
      setDocumentLoading(false);
    }
  };

  const handleDocumentClick = (document: Document) => {
    setSelectedDocument(document);
    setError(null);
    // Try to fetch document URL for PDFs, but don't require R2 key for now
    if (document.document_type?.includes('pdf')) {
      fetchDocumentUrl(document.id);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    // For now, show all documents since R2 fields may not be available yet
    // TODO: Filter by R2 key once migration is complete
    const matchesSearch = (doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
                         doc.file_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || doc.document_type === filterType;
    return matchesSearch && matchesType;
  });

  const getDocumentTypeColor = (type?: string) => {
    switch (type) {
      case 'performance_review': return 'bg-blue-100 text-blue-800';
      case 'offer_letter': return 'bg-green-100 text-green-800';
      case 'memo': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  const formatPeopleNames = (names?: string) => {
    if (!names) return null;
    return names.split(',').map(name => name.trim()).join(', ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Documents</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDocuments}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
                <p className="text-sm text-gray-600">View and manage your case documents</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {documents.length} documents
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="performance_review">Performance Reviews</option>
              <option value="offer_letter">Offer Letters</option>
              <option value="memo">Memos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((document) => (
            <div
              key={document.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleDocumentClick(document)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {document.title || document.file_name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">{document.file_name}</p>
                  </div>
                  <div className="ml-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDocumentTypeColor(document.document_type)}`}>
                      {document.document_type || 'Unknown'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  {document.document_date && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(document.document_date).toLocaleDateString()}
                    </div>
                  )}
                  <div className="flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    {document.file_name}
                  </div>
                  {document.people_names && (
                    <div className="flex items-center">
                      <Tag className="h-4 w-4 mr-2" />
                      <span className="line-clamp-1">{formatPeopleNames(document.people_names)}</span>
                    </div>
                  )}
                </div>

                {document.legal_relevance && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700 line-clamp-3">{document.legal_relevance}</p>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <button className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium">
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </button>
                  <button className="flex items-center text-gray-600 hover:text-gray-800 text-sm font-medium">
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredDocuments.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'No documents have been uploaded yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Document Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>
            
            <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-7xl h-full max-h-screen flex flex-col">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 flex-shrink-0">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">{selectedDocument.title || selectedDocument.file_name}</h3>
                  <div className="flex items-center space-x-4">
                    {selectedDocument.document_type?.includes('pdf') && (
                      <button
                        onClick={() => setShowSideBySide(!showSideBySide)}
                        className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        {showSideBySide ? 'PDF Only' : 'PDF + Text'}
                      </button>
                    )}
                    <button
                      onClick={handleCloseModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex-1 flex flex-col lg:flex-row min-h-0">
                {/* PDF Viewer */}
                <div className={`${showSideBySide ? 'flex-1' : 'w-full'} bg-gray-100 p-4 min-h-0`}>
                  {documentLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading document...</p>
                      </div>
                    </div>
                  ) : documentUrl ? (
                    <iframe
                      src={documentUrl}
                      className="w-full h-full border-0 rounded"
                      title={selectedDocument.title || selectedDocument.file_name}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium mb-2">Document Not Available</p>
                        <p className="text-sm">This document is not available for viewing.</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Extracted Text Panel (only shown in side-by-side mode) */}
                {showSideBySide && (
                  <div className="flex-1 bg-gray-50 p-4 min-h-0">
                    <div className="h-full overflow-y-auto">
                      <h4 className="font-medium text-gray-900 mb-3">Extracted Text</h4>
                      {selectedDocument.key_evidence ? (
                        <div className="prose prose-sm max-w-none">
                          <div className="bg-white p-4 rounded-lg border">
                            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                              {selectedDocument.key_evidence}
                            </pre>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">No extracted text available</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                  
                {/* Metadata */}
                <div className={`${showSideBySide ? 'w-80' : 'w-full lg:w-80'} bg-white p-4 overflow-y-auto`}>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Document Information</h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Filename:</dt>
                          <dd className="text-gray-900">{selectedDocument.file_name}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Type:</dt>
                          <dd className="text-gray-900">{selectedDocument.document_type || 'Unknown'}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Date:</dt>
                          <dd className="text-gray-900">
                            {selectedDocument.document_date 
                              ? new Date(selectedDocument.document_date).toLocaleDateString()
                              : 'Unknown'
                            }
                          </dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Category:</dt>
                          <dd className="text-gray-900">{selectedDocument.category || 'Unknown'}</dd>
                        </div>
                      </dl>
                    </div>

                    {selectedDocument.people_names && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">People Mentioned</h4>
                        <div className="flex flex-wrap gap-2">
                          {formatPeopleNames(selectedDocument.people_names)?.split(', ').map((name: string, index: number) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedDocument.legal_relevance && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Legal Relevance</h4>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                          {selectedDocument.legal_relevance}
                        </p>
                      </div>
                    )}

                    {selectedDocument.settlement_leverage_summary && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Settlement Leverage Summary</h4>
                        <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                          {selectedDocument.settlement_leverage_summary}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
